import { z } from 'zod';
import { pageInfoSchema } from './pagination';
import {
  SLACK_CHANNEL_CONFLICT_ACTIONS,
  SLACK_MISSING_USER_ACTIONS,
  asyncJobKindSchema,
  asyncJobStatusSchema,
  slackImportStatusSchema,
  workspaceExportStatusSchema,
} from '../enums';
import { isoDateTimeSchema, singleLineText, uuidSchema } from './common';

/**
 * Slack export import, workspace export and job status contracts (PLAN §4 "Slack export import
 * and workspace export", §3.1 `slack_imports`, `workspace_exports`, `async_jobs`; SPEC §4.8,
 * §4.9). Owned by the `import` API module (apps/api/src/modules/import) and the web extras
 * stream (features/slack-import). "Slack" names the source format only (D-011 branding rule).
 */

export const importIdParamsSchema = z.object({ importId: uuidSchema });
export const exportIdParamsSchema = z.object({ exportId: uuidSchema });
export const jobIdParamsSchema = z.object({ jobId: uuidSchema });

// ================================================================ jobs

/** GET /jobs/:jobId — the durable status of a long-running operation (`async_jobs`). */
export const jobDtoSchema = z.object({
  id: uuidSchema,
  kind: asyncJobKindSchema,
  status: asyncJobStatusSchema,
  progress: z.object({
    processed: z.number().int().nonnegative().optional(),
    total: z.number().int().nonnegative().optional(),
  }),
  /** An API path (never a signed URL). */
  resultUrl: z.string().nullable(),
  error: z.string().nullable(),
  createdAt: isoDateTimeSchema,
  finishedAt: isoDateTimeSchema.nullable(),
});
export type JobDto = z.infer<typeof jobDtoSchema>;

// ================================================================ Slack import

export const slackImportOptionsSchema = z.strictObject({
  importPrivateChannels: z.boolean().default(true),
  importDms: z.boolean().default(true),
  downloadFiles: z.boolean().default(true),
  /** `invite` only for addresses inside `auth.allowedEmailDomains` when that list is set. */
  createMissingUsersAs: z.enum(SLACK_MISSING_USER_ACTIONS).default('deactivated'),
  /** Imports never merge into an existing conversation. */
  channelConflict: z.enum(SLACK_CHANNEL_CONFLICT_ACTIONS).default('rename'),
});
export type SlackImportOptions = z.output<typeof slackImportOptionsSchema>;

/**
 * POST /admin/imports — upload the ZIP first (`/files/uploads`, `purpose: slack_import_archive`),
 * then start the analysis. `202 { import, job }`.
 */
export const startImportRequestSchema = z.strictObject({
  source: z.literal('slack_export'),
  fileId: uuidSchema,
  options: slackImportOptionsSchema.default({
    importPrivateChannels: true,
    importDms: true,
    downloadFiles: true,
    createMissingUsersAs: 'deactivated',
    channelConflict: 'rename',
  }),
});
export type StartImportRequest = z.infer<typeof startImportRequestSchema>;

export const importProgressSchema = z.object({
  phase: z.string(),
  processed: z.number().int().nonnegative(),
  total: z.number().int().nonnegative(),
  percent: z.number().min(0).max(100),
  currentChannel: z.string().nullable(),
});
export type ImportProgress = z.infer<typeof importProgressSchema>;

export const slackImportDtoSchema = z.object({
  id: uuidSchema,
  status: slackImportStatusSchema,
  createdBy: uuidSchema,
  createdAt: isoDateTimeSchema,
  archiveFileId: uuidSchema,
  progress: importProgressSchema.nullable(),
  /** Short counts once the dry run is ready. */
  summary: z
    .object({
      users: z.number().int().nonnegative(),
      channels: z.number().int().nonnegative(),
      messages: z.number().int().nonnegative(),
      files: z.number().int().nonnegative(),
    })
    .nullable(),
  errors: z.array(z.string()),
  analyzedAt: isoDateTimeSchema.nullable(),
  commitStartedAt: isoDateTimeSchema.nullable(),
  completedAt: isoDateTimeSchema.nullable(),
});
export type SlackImportDto = z.infer<typeof slackImportDtoSchema>;

export const startImportResponseSchema = z.object({
  import: slackImportDtoSchema,
  job: jobDtoSchema,
});
export type StartImportResponse = z.infer<typeof startImportResponseSchema>;

export const importListResponseSchema = z.object({
  items: z.array(slackImportDtoSchema),
  nextCursor: z.string().nullable(),
  page: pageInfoSchema.optional(),
});
export type ImportListResponse = z.infer<typeof importListResponseSchema>;

const userAction = z.enum(['match', 'create', 'skip']);
const channelAction = z.enum(['create', 'rename', 'skip']);

/** GET /admin/imports/:importId/report — the dry-run report (`409 report_not_ready` before). */
export const importReportSchema = z.object({
  users: z.object({
    total: z.number().int().nonnegative(),
    matched: z.number().int().nonnegative(),
    unmatched: z.array(
      z.object({
        sourceId: z.string(),
        name: z.string(),
        email: z.string().nullable(),
        action: userAction,
        userId: uuidSchema.nullable(),
      }),
    ),
  }),
  channels: z.array(
    z.object({
      sourceId: z.string(),
      name: z.string(),
      type: z.enum(['public_channel', 'private_channel', 'dm', 'group_dm']),
      messageCount: z.number().int().nonnegative(),
      action: channelAction,
      newName: z.string().nullable(),
    }),
  ),
  dms: z.object({
    count: z.number().int().nonnegative(),
    messages: z.number().int().nonnegative(),
  }),
  messages: z.object({
    total: z.number().int().nonnegative(),
    threads: z.number().int().nonnegative(),
    reactions: z.number().int().nonnegative(),
    pins: z.number().int().nonnegative(),
  }),
  files: z.object({
    count: z.number().int().nonnegative(),
    totalBytes: z.number().int().nonnegative(),
  }),
  dateRange: z.object({ from: isoDateTimeSchema.nullable(), to: isoDateTimeSchema.nullable() }),
  /** e.g. unsupported message subtypes with counts, missing files, unknown users. */
  warnings: z.array(z.object({ code: z.string(), message: z.string(), count: z.number().int() })),
});
export type ImportReport = z.infer<typeof importReportSchema>;

/** PATCH /admin/imports/:importId/mapping — only in `dry_run_ready` (`409 import_not_in_dry_run`). */
export const updateImportMappingRequestSchema = z.strictObject({
  users: z
    .array(
      z.strictObject({
        sourceId: z.string().min(1).max(32),
        action: userAction,
        userId: uuidSchema.optional(),
      }),
    )
    .max(10_000)
    .optional(),
  channels: z
    .array(
      z.strictObject({
        sourceId: z.string().min(1).max(32),
        action: channelAction,
        newName: z.string().trim().min(1).max(80).optional(),
      }),
    )
    .max(10_000)
    .optional(),
});
export type UpdateImportMappingRequest = z.infer<typeof updateImportMappingRequestSchema>;

/** POST /admin/imports/:importId/commit — resumable and idempotent (`slack_import_id_maps`). */
export const commitImportRequestSchema = z.strictObject({ acceptWarnings: z.literal(true) });
export type CommitImportRequest = z.infer<typeof commitImportRequestSchema>;

export const commitImportResponseSchema = z.object({ job: jobDtoSchema });

// ================================================================ workspace export

export const exportIncludeSchema = z.strictObject({
  publicChannels: z.boolean(),
  /** Owner only (with `dms`), and `confirmPhrase` is required. */
  privateChannels: z.boolean(),
  dms: z.boolean(),
  /** Only files referenced by rows in the included scopes. */
  files: z.boolean(),
  /** Phase 4. */
  tasks: z.boolean().default(false),
  /** Phase 5. */
  ptstdm: z.boolean().default(false),
  auditLog: z.boolean(),
});
export type ExportInclude = z.output<typeof exportIncludeSchema>;

/** POST /admin/exports — `202 { export, job }`; audit `export.requested`. */
export const requestExportRequestSchema = z.strictObject({
  include: exportIncludeSchema,
  from: isoDateTimeSchema.optional(),
  to: isoDateTimeSchema.optional(),
  reason: singleLineText(1, 500),
  /** The workspace name typed exactly, when private channels or DMs are included. */
  confirmPhrase: z.string().trim().max(80).optional(),
});
export type RequestExportRequest = z.infer<typeof requestExportRequestSchema>;

export const workspaceExportDtoSchema = z.object({
  id: uuidSchema,
  status: workspaceExportStatusSchema,
  requestedBy: uuidSchema,
  include: exportIncludeSchema,
  progress: z.record(z.string(), z.unknown()),
  sizeBytes: z.number().int().nonnegative().nullable(),
  /** Hex SHA-256 of the ZIP. */
  sha256: z.string().nullable(),
  /** `completedAt + 7 days`. */
  expiresAt: isoDateTimeSchema.nullable(),
  downloadCount: z.number().int().nonnegative(),
  error: z.string().nullable(),
  createdAt: isoDateTimeSchema,
  completedAt: isoDateTimeSchema.nullable(),
});
export type WorkspaceExportDto = z.infer<typeof workspaceExportDtoSchema>;

export const requestExportResponseSchema = z.object({
  export: workspaceExportDtoSchema,
  job: jobDtoSchema,
});
export type RequestExportResponse = z.infer<typeof requestExportResponseSchema>;

export const exportListResponseSchema = z.object({
  items: z.array(workspaceExportDtoSchema),
  nextCursor: z.string().nullable(),
  page: pageInfoSchema.optional(),
});
export type ExportListResponse = z.infer<typeof exportListResponseSchema>;

/** GET /admin/exports/:exportId/download-url — the requester or an Owner; audit `export.downloaded`. */
export const exportDownloadUrlResponseSchema = z.object({
  url: z.string(),
  expiresAt: isoDateTimeSchema,
});
